import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Import date-fns helpers
import addDays from "date-fns/addDays";
import isBefore from "date-fns/isBefore";
import isAfter from "date-fns/isAfter";
import set from "date-fns/set";

import { useNavigate } from "react-router-dom";

// Update these imports to match your actual file locations in "src" or "public"
import covidConsentForm from "./mrna-covid-19-vaccine-consent-form-12-plus-years-of-age.pdf";
import fluConsentForm from "./2024-2025-healthcare-provider-influenza-vaccine-consent-form.pdf";

const VaccineBookingModal = ({ open, onClose, onSubmit }) => {
  // Step 1: Vaccine details
  const [vaccineType, setVaccineType] = useState("");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [appointments, setAppointments] = useState(null);
  const [status, setStatus] = useState("idle");
  const [phoneErr, setPhoneErr] = useState("");
  const [emailErr, setEmailErr] = useState("");

  // Step control (1: vaccine details, 2: number of people, 3: person-by-person info)
  const [step, setStep] = useState(1);

  // Step 2: Number of people
  const [numberOfPeople, setNumberOfPeople] = useState(1);

  // Step 3: People data
  const [people, setPeople] = useState([]);
  const [currentPersonIndex, setCurrentPersonIndex] = useState(0);

  const navigate = useNavigate();

  // ---------- APPOINTMENTS FETCH & UTILS ----------
  const fetchAppointments = async () => {
    const currentDate = new Date().toISOString().split("T")[0];
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/appointments/${currentDate}`
      );
      const data = await response.json();
      const formattedData = formatAppointments(data);
      const indexedAppointments = indexAppointmentsByDate(formattedData);
      setAppointments(indexedAppointments);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
  };

  const formatAppointments = (appointments) => {
    return appointments.map((appointment) => {
      const formattedDate = new Date(appointment.date)
        .toISOString()
        .split("T")[0];
      const formattedTime = appointment.start_time.slice(0, 5);
      return {
        ...appointment,
        date: formattedDate,
        start_time: formattedTime,
      };
    });
  };

  const indexAppointmentsByDate = (appointments) => {
    const indexed = {};
    appointments.forEach((appointment) => {
      const { date, start_time } = appointment;
      const [hour, minute] = start_time.split(":");
      const hourInt = parseInt(hour, 10);
      const ampm = hourInt >= 12 ? "PM" : "AM";
      const formattedHour = hourInt % 12 === 0 ? 12 : hourInt % 12;
      const formattedTime = `${formattedHour}:${minute} ${ampm}`;
      if (!indexed[date]) {
        indexed[date] = [];
      }
      indexed[date].push(formattedTime);
    });
    return indexed;
  };

  useEffect(() => {
    if (open) {
      fetchAppointments();
      setStatus("idle");
      // Reset steps and form data when modal opens.
      setStep(1);
      setVaccineType("");
      setDate(null);
      setTime(null);
      setNumberOfPeople(1);
      setPeople([]);
      setCurrentPersonIndex(0);
    }
  }, [open]);

  // ---------- PHARMACY HOURS & TIME SLOTS ----------
  const hours = {
    Monday: { start: "10:50", end: "17:50" },
    Tuesday: { start: "10:50", end: "18:10" },
    Wednesday: { start: "10:50", end: "18:10" },
    Thursday: { start: "10:50", end: "18:10" },
    Friday: { start: "10:50", end: "17:50" },
    Saturday: { start: "10:50", end: "13:50" },
    Sunday: null,
  };

  const generateTimeSlots = (selectedDate) => {
    if (!selectedDate) return [];
    const dayOfWeek = selectedDate.toLocaleDateString("en-US", {
      weekday: "long",
    });
    const dayHours = hours[dayOfWeek];
    if (!dayHours) return [];

    const dateKey = selectedDate.toISOString().split("T")[0];
    const slots = [];
    let current = set(new Date(), {
      hours: parseInt(dayHours.start.split(":")[0], 10),
      minutes: parseInt(dayHours.start.split(":")[1], 10),
      seconds: 0,
      milliseconds: 0,
    });
    const end = set(new Date(), {
      hours: parseInt(dayHours.end.split(":")[0], 10),
      minutes: parseInt(dayHours.end.split(":")[1], 10),
    });

    while (isBefore(current, end)) {
      let timeSlot = current.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      if (timeSlot.startsWith("0")) {
        timeSlot = timeSlot.substring(1);
      }
      // Check if slot is already taken
      if (appointments && appointments[dateKey]?.includes(timeSlot)) {
        // Skip booked slot
      } else {
        slots.push(timeSlot);
      }
      current = new Date(current.getTime() + 20 * 60000); // +20 minutes
    }
    return slots;
  };

  // --------- NEW: DISABLE DATES BEFORE 3 DAYS FROM NOW ---------
  // Also keep the 14-day max window.
  const shouldDisableDate = (dateToCheck) => {
    const minDate = addDays(new Date(), 3); // earliest allowed day
    const maxDate = addDays(new Date(), 14); // latest allowed day
    return isBefore(dateToCheck, minDate) || isAfter(dateToCheck, maxDate);
  };

  const timeSlots = generateTimeSlots(date);

  // ---------- STEP 1 HANDLER ----------
  const handleStep1Next = () => {
    if (!vaccineType || !date || !time) {
      alert("Please select vaccine type, date, and time.");
      return;
    }
    setStep(2);
  };

  // ---------- STEP 2 HANDLER ----------
  const handleNumberOfPeopleChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setNumberOfPeople(value);
  };

  const handleStep2Next = () => {
    // Initialize the people array with empty objects
    const initialPeople = Array.from({ length: numberOfPeople }, () => ({
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      province: "",
    }));
    setPeople(initialPeople);
    setCurrentPersonIndex(0);
    setStep(3);
  };

  // ---------- STEP 3 HANDLERS ----------
  const handlePersonChange = (field, value) => {
    const updated = [...people];
    updated[currentPersonIndex][field] = value;
    setPeople(updated);
  };

  const validateCurrentPerson = () => {
    const person = people[currentPersonIndex];
    if (
      !person.firstName ||
      !person.lastName ||
      !person.dateOfBirth ||
      !person.address ||
      !person.city ||
      !person.province
    ) {
      alert("Please complete all required fields.");
      return false;
    }
    // For the primary booking contact (index 0), email and phone are required.
    if (currentPersonIndex === 0) {
      if (!person.email || !person.phone) {
        alert(
          "Please complete the email and phone for the primary booking contact."
        );
        return false;
      }
    }
    return true;
  };

  const handleStep3Next = () => {
    if (!validateCurrentPerson()) return;
    if (computeErrors(people[currentPersonIndex])) return;
    if (currentPersonIndex < numberOfPeople - 1) {
      setCurrentPersonIndex(currentPersonIndex + 1);
    } else {
      // Last person: submit all booking details.
      handleSubmit();
    }
  };

  const handleStep3Back = () => {
    if (currentPersonIndex > 0) {
      setCurrentPersonIndex(currentPersonIndex - 1);
    } else {
      // If first person, go back to step 2.
      setStep(2);
    }
  };

  // ---------- FINAL SUBMIT ----------
  const handleSubmit = async () => {
    // Validate all persons
    for (let i = 0; i < numberOfPeople; i++) {
      const person = people[i];
      if (
        !person.firstName ||
        !person.lastName ||
        !person.dateOfBirth ||
        !person.address ||
        !person.city ||
        !person.province ||
        (i === 0 && (!person.email || !person.phone))
      ) {
        alert(`Please complete all required fields for Person #${i + 1}.`);
        return;
      }
    }
    try {
      const response = await onSubmit({
        vaccineType,
        date,
        time,
        numberOfPeople,
        people,
      });
      if (response?.status === 201) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch (error) {
      setStatus("error");
    }
  };

  // ---------- CLOSE HANDLER ----------
  const handleClose = () => {
    onClose();
    setStep(1);
    setStatus("idle");
    setVaccineType("");
    setDate(null);
    setTime(null);
    setNumberOfPeople(1);
    setPeople([]);
    setCurrentPersonIndex(0);
  };

  // --- email & phone validation helpers ---
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const onlyDigits = /^[0-9]*$/;

  const computeErrors = (person) => {
    // only for primary contact
    if (currentPersonIndex === 0) {
      if (person.email && !emailRegex.test(person.email)) {
        alert("Invalid email address");
        return true;
      }
      if (person.phone && !onlyDigits.test(person.phone)) {
        alert("Only numbers allowed in phone number");
        return true;
      } else if (
        person.phone &&
        (person.phone.length < 10 || person.phone.length > 11)
      ) {
        alert("Phone number must be 10–11 digits");
        return true;
      }
    }
    if (person.dateOfBirth) {
      const dob = new Date(person.dateOfBirth);
      const today = new Date();
      dob.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (dob > today) {
        alert("Date of birth can’t be in the future.");
        return true;
      }
    }
  };

  // ---------- RENDER ----------
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: "auto",
          maxHeight: "650px",
        }}
      >
        {/* STEP 1 */}
        {status === "idle" && step === 1 && (
          <>
            <Typography variant="h6" mb={2}>
              Request a Vaccine Appointment (Step 1)
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Vaccine</InputLabel>
              <Select
                value={vaccineType}
                onChange={(e) => setVaccineType(e.target.value)}
                label="Select Vaccine"
              >
                {/* Vaccine options here */}
                <MenuItem value="Bacille Calmette-Guerin BCGVaccines">
                  Bacille Calmette-Guerin (BCG)
                </MenuItem>
                <MenuItem value="Haemophilus Influenzae type b (Hib) Vaccines">
                  Haemophilus Influenzae type b (Hib)
                </MenuItem>
                <MenuItem value="Meningococcal Vaccines">
                  Meningococcal Vaccines
                </MenuItem>
                <MenuItem value="Covid-19 Vaccines">Covid-19 Vaccines</MenuItem>
                <MenuItem value="Pneumococcal Vaccines">
                  Pneumococcal Vaccines
                </MenuItem>
                <MenuItem value="Typhoid Vaccines">Typhoid Vaccines</MenuItem>
                <MenuItem value="Combined Typhoid and Hepatitis A Vaccines">
                  Combined Typhoid and Hep A
                </MenuItem>
                <MenuItem value="Hepatitis A Vaccines">
                  Hepatitis A Vaccines
                </MenuItem>
                <MenuItem value="Hepatitis B Vaccines">
                  Hepatitis B Vaccines
                </MenuItem>
                <MenuItem value="Hepatitis A and B combined Vaccines">
                  Hep A and B Combined
                </MenuItem>
                <MenuItem value="Herpes Zoster Vaccines">
                  Herpes Zoster Vaccines
                </MenuItem>
                <MenuItem value="Human Papillomavirus (HPV) Vaccines">
                  HPV Vaccines
                </MenuItem>
                <MenuItem value="Japanese Encephalitis Vaccines">
                  Japanese Encephalitis
                </MenuItem>
                <MenuItem value="Rabies Vaccines">Rabies Vaccines</MenuItem>
                <MenuItem value="Varicella Vaccines">
                  Varicella Vaccines
                </MenuItem>
                <MenuItem value="Yellow Fever Vaccines">
                  Yellow Fever Vaccines
                </MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box className="grid grid-cols-1 sm:grid-cols-2 gap-2 ">
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={date}
                  onChange={(newValue) => {
                    setDate(newValue);
                    setTime(null);
                  }}
                  // Restrict selection to [3 days from now ... 14 days from now]
                  minDate={addDays(new Date(), 3)}
                  maxDate={addDays(new Date(), 14)}
                  shouldDisableDate={shouldDisableDate}
                  renderInput={(params) => <TextField {...params} />}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" mb={1}>
                    Select Time Slot
                  </Typography>
                  <Grid container spacing={1}>
                    {timeSlots.length > 0 ? (
                      timeSlots.map((slot, index) => (
                        <Grid item xs={4} sm={3} key={index}>
                          <Button
                            variant={time === slot ? "contained" : "outlined"}
                            color="primary"
                            fullWidth
                            sx={{ fontSize: "13.7px" }}
                            onClick={() => setTime(slot)}
                          >
                            {slot}
                          </Button>
                        </Grid>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No available slots for this day.
                      </Typography>
                    )}
                  </Grid>
                </Box>
              </Box>
            </LocalizationProvider>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
            >
              <Button onClick={handleClose} variant="outlined" color="error">
                Close
              </Button>
              <Button
                onClick={handleStep1Next}
                variant="contained"
                color="primary"
              >
                Next
              </Button>
            </Box>
          </>
        )}

        {/* STEP 2 */}
        {status === "idle" && step === 2 && (
          <>
            <Typography variant="h6" mb={2}>
              Request a Vaccine Appointment (Step 2)
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Number of People</InputLabel>
              <Select
                value={numberOfPeople}
                onChange={handleNumberOfPeopleChange}
                label="Number of People"
              >
                {[1, 2, 3, 4, 5].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button onClick={() => setStep(1)} variant="outlined">
                Back
              </Button>
              <Button
                onClick={handleStep2Next}
                variant="contained"
                color="primary"
                sx={{ ml: 2 }}
              >
                Next
              </Button>
            </Box>
          </>
        )}

        {/* STEP 3 */}
        {status === "idle" && step === 3 && (
          <>
            <Typography variant="h6" mb={2}>
              {currentPersonIndex === 0
                ? `Primary Booking Contact Information - Person 1 of ${numberOfPeople}`
                : `Additional Person ${currentPersonIndex + 1}`}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={people[currentPersonIndex]?.firstName || ""}
                  onChange={(e) =>
                    handlePersonChange("firstName", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={people[currentPersonIndex]?.lastName || ""}
                  onChange={(e) =>
                    handlePersonChange("lastName", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of Birth"
                  InputLabelProps={{ shrink: true }}
                  value={people[currentPersonIndex]?.dateOfBirth || ""}
                  onChange={(e) =>
                    handlePersonChange("dateOfBirth", e.target.value)
                  }
                />
              </Grid>

              {/* Primary contact fields only for personIndex 0 */}
              {currentPersonIndex === 0 && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={people[currentPersonIndex]?.email || ""}
                      onChange={(e) =>
                        handlePersonChange("email", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      type="tel"
                      value={people[currentPersonIndex]?.phone || ""}
                      onChange={(e) =>
                        handlePersonChange("phone", e.target.value)
                      }
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Address"
                  value={people[currentPersonIndex]?.address || ""}
                  onChange={(e) =>
                    handlePersonChange("address", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={people[currentPersonIndex]?.city || ""}
                  onChange={(e) => handlePersonChange("city", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Province"
                  value={people[currentPersonIndex]?.province || ""}
                  onChange={(e) =>
                    handlePersonChange("province", e.target.value)
                  }
                />
              </Grid>

              {/* Optional: Display consent form link for specific vaccines */}
              {(vaccineType === "Covid-19 Vaccines" ||
                vaccineType ===
                  "Haemophilus Influenzae type b (Hib) Vaccines") && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="textSecondary">
                    Please download the consent form below, fill it out, and
                    bring it to your appointment to save time.
                  </Typography>
                  <Button
                    variant="outlined"
                    component="a"
                    href={
                      vaccineType === "Covid-19 Vaccines"
                        ? covidConsentForm
                        : fluConsentForm
                    }
                    download
                    sx={{ mt: 1 }}
                  >
                    Download Consent Form
                  </Button>
                </Grid>
              )}
            </Grid>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
            >
              <Button onClick={handleStep3Back} variant="outlined">
                Back
              </Button>
              <Button
                onClick={handleStep3Next}
                variant="contained"
                color="primary"
              >
                {currentPersonIndex === numberOfPeople - 1 ? "Submit" : "Next"}
              </Button>
            </Box>
          </>
        )}

        {/* SUCCESS */}
        {status === "success" && (
          <Box textAlign="center">
            <Typography variant="h6" color="primary">
              Appointment Booked Successfully!
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate("/");
                handleClose();
              }}
              sx={{ mt: 2 }}
            >
              Return To Home
            </Button>
          </Box>
        )}

        {/* ERROR */}
        {status === "error" && (
          <Box textAlign="center">
            <Typography variant="h6" color="error">
              Booking Failed
            </Typography>
            <Typography variant="body2" mt={1}>
              Sorry, this appointment might already be taken or there was a
              problem. Please try again or call the pharmacy if the issue
              persists.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setStatus("idle")}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default VaccineBookingModal;
